import { FormControl, FormGroup } from "@angular/forms";
import type {
  VILCOpportunitiesComponent,
  VILCOpportunity,
} from "./vilc-opportunities.component";
import type { VilcWorkflowDependencies } from "./vilc-opportunities-workflow-dependencies";

type VilcRowsWorkflowContext = Pick<
  VILCOpportunitiesComponent,
  | "opportunities"
  | "rows"
  | "totalItems"
  | "currentPage"
  | "prepareForms"
  | "formMap"
  | "actionButtonCall"
  | "pageSize"
  | "startIndex"
  | "totalPages"
  | "endIndex"
>;
export class VilcRowsWorkflow {
  loadData(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): void {
    if (context.opportunities?.length) {
      context.rows = [...context.opportunities];
    }
    context.totalItems = context.rows.length;
    context.currentPage = 1;
    context.prepareForms();
  }
  prepareForms(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): void {
    context.formMap = {};
    context.rows.forEach((r, index) => {
      context.formMap[index] = new FormGroup({
        financially_viable: new FormControl(r.financially_viable),
        have_new_value: new FormControl(r.have_new_value),
        new_vilc_hl: new FormControl(r.new_vilc_hl),
      });
    });
  }
  setViable(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    row: VILCOpportunity,
    value: boolean,
  ): void {
    const index = context.rows.indexOf(row);
    row.financially_viable = value;
    if (!value) {
      row.new_vilc_hl = null;
      row.have_new_value = null;
      context.formMap[index].get("new_vilc_hl")?.setValue(null);
      context.formMap[index].get("have_new_value")?.setValue(null);
    }
    context.formMap[index].get("financially_viable")?.setValue(value);
  }
  startEdit(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    row: VILCOpportunity,
  ): void {
    const index = context.rows.indexOf(row);
    row.financially_viable = null;
    row.have_new_value = null;
    row.new_vilc_hl = null;
    context.formMap[index].reset({
      financially_viable: null,
      have_new_value: null,
      new_vilc_hl: null,
    });
  }
  sethave_new_value(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    row: VILCOpportunity,
    value: boolean | null,
  ): void {
    const index = context.rows.indexOf(row);
    row.have_new_value = value;
    if (value === false) {
      row.new_vilc_hl = null;
      context.formMap[index].get("new_vilc_hl")?.setValue(null);
    }
    context.formMap[index].get("have_new_value")?.setValue(value);
  }
  onnew_vilc_hlChange(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    row: VILCOpportunity,
    value: any,
  ): void {
    const index = context.rows.indexOf(row);
    if (index < 0) return;

    const raw =
      typeof value === "string"
        ? value.trim()
        : typeof value === "number"
          ? String(value)
          : (value ?? "");

    const sanitized = raw.replace(/[^0-9.-]/g, "");
    const isIncomplete =
      sanitized === "" ||
      sanitized === "-" ||
      sanitized === "." ||
      sanitized === "-.";

    const numericValue = isIncomplete ? null : Number(sanitized);

    row.new_vilc_hl = numericValue;
    context.formMap[index].get("new_vilc_hl")?.setValue(numericValue);
  }
  isSimulationDisabled(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): boolean {
    return !context.rows.every((r) => {
      if (r.financially_viable === null || r.financially_viable === undefined) {
        return false;
      }
      if (r.financially_viable === true) {
        if (r.have_new_value === null || r.have_new_value === undefined) {
          return false;
        }
        if (
          r.have_new_value === true &&
          (r.new_vilc_hl === null || r.new_vilc_hl === undefined)
        ) {
          return false;
        }
      }
      return true;
    });
  }
  onSimulate(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): void {
    const updatedVilcTablePayloadData = context.rows.map((r) => ({
      sku: r.sku,
      financially_viable: r.financially_viable,
      new_vilc_hl: r.financially_viable ? (r.new_vilc_hl ?? null) : null,
    }));
    context.actionButtonCall.emit(updatedVilcTablePayloadData);
  }
  restrictToNumeric(
    _context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    event: KeyboardEvent,
    currentValue: string = "",
  ): void {
    const input = event.target as HTMLInputElement | null;
    const value = input?.value ?? currentValue;
    const selectionStart =
      input?.selectionStart !== null && input?.selectionStart !== undefined
        ? input.selectionStart
        : value.length;

    const allowedControlKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedControlKeys.includes(event.key)) {
      return;
    }

    const isDigit = /[0-9]/.test(event.key);
    const isDot = event.key === ".";
    const isMinus = event.key === "-";
    const alreadyHasDot = value.includes(".");
    const alreadyHasMinus = value.includes("-");

    if (isDigit) {
      return;
    }

    if (isDot) {
      if (alreadyHasDot) {
        event.preventDefault();
      }
      return;
    }

    if (isMinus) {
      const selectionEnd = input?.selectionEnd ?? value.length;
      const wholeSelection =
        selectionStart === 0 && selectionEnd === value.length;
      if (alreadyHasMinus && !wholeSelection) {
        event.preventDefault();
        return;
      }
      if (selectionStart !== 0) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
  }
  totalPages(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): number {
    return Math.ceil(context.totalItems / context.pageSize);
  }
  startIndex(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): number {
    return (context.currentPage - 1) * context.pageSize;
  }
  endIndex(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): number {
    return Math.min(context.startIndex + context.pageSize, context.totalItems);
  }
  paginatedRows(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): VILCOpportunity[] {
    if (context.totalItems === 0) {
      return [];
    }
    if (context.currentPage < 1) {
      context.currentPage = 1;
    } else if (context.currentPage > context.totalPages) {
      context.currentPage = context.totalPages;
    }
    return context.rows.slice(context.startIndex, context.endIndex);
  }
  onPageSizeChange(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    event: any,
  ): void {
    context.pageSize = Number(event.target.value);
    context.currentPage = 1;
  }
  prevPage(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): void {
    if (context.currentPage > 1) context.currentPage--;
  }
  nextPage(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
  ): void {
    if (context.currentPage < context.totalPages) context.currentPage++;
  }
  onPageInputChange(
    context: VilcRowsWorkflowContext,
    _dependencies: VilcWorkflowDependencies,
    event: Event,
  ): void {
    const input = (event.target as HTMLInputElement).value;
    let page = Number(input);
    if (isNaN(page) || page < 1) {
      page = 1;
    } else if (page > context.totalPages) {
      page = context.totalPages;
    }
    context.currentPage = page;
  }
}
